import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Plus, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading, Magnetic } from "@/components/common/motion";
import { faqs, testimonials, company } from "@/data/site";
import { cn } from "@/lib/utils";

/* Reusable CTA band */
export function ContactCTA() {
  return (
    <section className="relative overflow-hidden bg-secondary py-24 text-secondary-foreground grain" data-testid="contact-cta">
      <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary/30 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-96 w-96 rounded-full bg-accent/20 blur-[120px]" />
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Start your project</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight text-white md:text-5xl text-balance">
            Ready to turn your idea into reliable, scalable technology?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/70">
            Book a free consultation. We&apos;ll listen, advise honestly and follow up within one business day.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Magnetic>
              <Link
                to="/contact"
                data-testid="cta-schedule"
                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg transition-shadow hover:shadow-xl"
              >
                Schedule Free Consultation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <a
              href={`mailto:${company.email}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Talk to an Expert
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* Reusable FAQ accordion */
export function FAQSection({ limit }) {
  const [open, setOpen] = useState(0);
  const list = limit ? faqs.slice(0, limit) : faqs;
  return (
    <section className="py-24 sm:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading align="center" overline="FAQ" title="Questions, answered" subtitle="Everything you need to know about working with NeuroTriq TechLabs." />
        </Reveal>
        <div className="mt-12 divide-y divide-border rounded-2xl border border-border bg-card">
          {list.map((f, i) => (
            <div key={i} data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left"
                data-testid={`faq-trigger-${i}`}
              >
                <span className="font-display text-base font-medium md:text-lg">{f.q}</span>
                <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border transition-all duration-300", open === i && "rotate-45 bg-primary text-primary-foreground")}>
                  <Plus className="h-4 w-4" />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 text-muted-foreground leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Reusable testimonials carousel */
export function Testimonials() {
  const [i, setI] = useState(0);
  const t = testimonials[i];
  const go = (dir) => setI((p) => (p + dir + testimonials.length) % testimonials.length);
  return (
    <section className="bg-muted py-24 sm:py-32" data-testid="testimonials">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading overline="Testimonials" title="Trusted by leaders who ship" subtitle="Real outcomes from teams who partnered with NeuroTriq TechLabs." />
        </Reveal>
        <div className="mt-14 grid items-center gap-10 lg:grid-cols-[1fr_auto]">
          <AnimatePresence mode="wait">
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-12"
              data-testid="testimonial-card"
            >
              <Quote className="h-10 w-10 text-primary/25" />
              <blockquote className="mt-4 font-display text-xl font-medium leading-relaxed tracking-tight md:text-2xl text-balance">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8 flex items-center gap-4">
                <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover" loading="lazy" />
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </figcaption>
            </motion.figure>
          </AnimatePresence>

          <div className="flex gap-3 lg:flex-col">
            <button onClick={() => go(-1)} data-testid="testimonial-prev" aria-label="Previous" className="grid h-12 w-12 place-items-center rounded-full border border-border bg-card transition-colors hover:bg-primary hover:text-primary-foreground">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => go(1)} data-testid="testimonial-next" aria-label="Next" className="grid h-12 w-12 place-items-center rounded-full border border-border bg-card transition-colors hover:bg-primary hover:text-primary-foreground">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="mt-8 flex gap-2">
          {testimonials.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              aria-label={`Go to testimonial ${idx + 1}`}
              className={cn("h-1.5 rounded-full transition-all", idx === i ? "w-8 bg-primary" : "w-4 bg-border")}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
